/**
 * Guards async state updates against responses that arrive after the user moved on.
 *
 * Requests are scoped to an "active key" (e.g. the currently open customer's id): `setActive(key)` records which key
 * may currently start a request, and `start(key)` returns `null` (without advancing) when `key` is not the active
 * key, so a request for a customer that is no longer open never even gets a number. Once started, check
 * `isCurrent(id)` before writing its result to state. `invalidate()` makes everything in flight obsolete and clears
 * the active key (e.g. when a modal closes), so a late answer for customer A can never land on customer B.
 */
export interface LatestRequestTracker {
  setActive: (key: string | null) => void;
  start: (key: string) => number | null;
  isCurrent: (id: number) => boolean;
  invalidate: () => void;
}

export function createLatestRequestTracker(): LatestRequestTracker {
  let current = 0;
  let activeKey: string | null = null;
  return {
    setActive: key => {
      // A different customer makes anything still in flight for the previous one obsolete right away.
      if (key !== activeKey) current += 1;
      activeKey = key;
    },
    start: key => {
      if (key !== activeKey) return null;
      current += 1;
      return current;
    },
    isCurrent: id => id === current,
    invalidate: () => {
      current += 1;
      activeKey = null;
    },
  };
}
